import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  diagOverview, diagMatters, diagMatterDetail,
  diagLiveQuery, diagDocumentTree, diagTables,
  diagTableSample, diagReindex, diagListQcLog,
  diagExportMatter, diagExportAllMatters,
  diagListSessions, diagSessionEvents,
  diagMatterLlmCalls,
  diagMatterSpecSections, diagMatterSpecSectionDetail,
  diagMatterFigures, diagMatterFigureDetail,
  cleanupPreview, cleanupExecute,
  type QcLogRow, type UserSessionRow, type UserEventRow,
  type SessionEventsResponse,
  type LlmCallRow, type LlmCallsResponse,
  type SpecSectionSummary, type SpecSectionDetailResponse,
  type FigureSummary, type FigureDetailResponse,
  type CleanupReport, type CleanupExecuteResult,
} from '@/api/client';
import AppFooter from '@/components/layout/AppFooter';

import { apiErrorText } from '../../lib/describeApiError';
/**
 * /admin/diagnostic — read-only inspector for the data state of a
 * matter, the database, and the PageIndex pool. Admin-only at the
 * server (every endpoint behind require_admin → 403); the panel
 * surfaces a clear "Admin access required" when the API rejects.
 *
 * Native HTML throughout (sidesteps the untyped shadcn forwardRef
 * wrappers used elsewhere). Auto-refresh every 30s on the overview.
 */

interface Props {
  onBack?: () => void;
}

const inputClass =
  'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1.5 ' +
  'text-sm focus-visible:outline-none focus-visible:ring-2 ' +
  'focus-visible:ring-ring disabled:opacity-50';
const primaryBtn =
  'inline-flex h-9 items-center justify-center rounded-md bg-primary px-3 ' +
  'text-sm font-medium text-primary-foreground hover:bg-primary/90 ' +
  'disabled:opacity-50';
const outlineBtn =
  'inline-flex h-9 items-center justify-center rounded-md border border-input ' +
  'bg-background px-3 text-sm font-medium hover:bg-accent disabled:opacity-60';

const DOC_TYPES = [
  'idf', 'prior_art', 'frozen_claim', 'technical_understanding',
  'novelty_analysis', 'inventive_step', 'claim_strategy', 'qa_round',
];

type Tab =
  | 'overview' | 'documents' | 'index_pool' | 'claims'
  | 'qa' | 'qc_log' | 'spec_sections' | 'figures'
  | 'llm_calls' | 'live_query' | 'tables';

type Matter = {
  matter_id: string;
  title?: string;
  status?: string | null;
  current_stage?: string;
  stage_reached?: string;
  workspace_id?: number | null;
  owner_id?: number | null;
  created_at?: string;
  claim_count: number;
  frozen_claim_count: number;
  document_count: number;
  indexed_document_count: number;
};

type DocRow = {
  id: number;
  doc_type: string;
  doc_name: string;
  status: string;
  has_images: boolean;
  vision_processed: boolean;
  indexed_at: string | null;
  uploaded_at: string;
  tree_json_size: number;
  tree_preview: string;
};

function Card({
  label, value, sub,
}: { label: string; value: string | number; sub?: string }) {
  return (
    <div className="border border-zinc-200 rounded-md bg-white p-4 min-w-[150px]">
      <div className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="text-2xl font-semibold text-zinc-900 mt-1">{value}</div>
      {sub && <div className="text-[11px] text-zinc-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function StatusBadge({ status }: { status: string | undefined | null }) {
  const palette: Record<string, string> = {
    indexed: 'bg-emerald-100 text-emerald-800',
    processing: 'bg-amber-100 text-amber-800',
    failed: 'bg-red-100 text-red-800',
    uploaded: 'bg-zinc-100 text-zinc-800',
    archived: 'bg-zinc-200 text-zinc-600',
  };
  const cls = palette[status || ''] || 'bg-zinc-100 text-zinc-800';
  return (
    <span className={`px-2 py-0.5 text-[11px] rounded ${cls}`}>{status || 'n/a'}</span>
  );
}

export default function DiagnosticPage({ onBack }: Props) {
  const [overview, setOverview] = useState<any>(null);
  const [overviewError, setOverviewError] = useState<string | null>(null);
  const [matters, setMatters] = useState<Matter[]>([]);
  const [matterFilter, setMatterFilter] = useState('');
  const [activeMatter, setActiveMatter] = useState<string | null>(null);
  const [detail, setDetail] = useState<any>(null);
  const [tab, setTab] = useState<Tab>('overview');
  const [loadingDetail, setLoadingDetail] = useState(false);

  // ── Overview / matters list, refreshed every 30s ────────
  const refreshOverview = useCallback(async () => {
    try {
      const [ov, ml] = await Promise.all([diagOverview(), diagMatters()]);
      setOverview(ov);
      setMatters(ml.matters || []);
      setOverviewError(null);
    } catch (e: any) {
      setOverviewError(
        e?.response?.status === 403
          ? 'Admin access required.'
          : 'Failed to load diagnostic data.',
      );
    }
  }, []);

  useEffect(() => {
    refreshOverview();
    const t = window.setInterval(refreshOverview, 30000);
    return () => window.clearInterval(t);
  }, [refreshOverview]);

  // ── Matter detail loader ────────────────────────────────
  const loadDetail = useCallback(async (mid: string) => {
    setLoadingDetail(true);
    try {
      setDetail(await diagMatterDetail(mid));
    } catch (e: any) {
      setDetail({ error: apiErrorText(e, 'Failed to load matter detail') });
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  useEffect(() => {
    if (activeMatter) loadDetail(activeMatter);
    else setDetail(null);
  }, [activeMatter, loadDetail]);

  const filtered = useMemo(() => {
    const f = matterFilter.trim().toLowerCase();
    if (!f) return matters;
    return matters.filter((m) =>
      (m.title || '').toLowerCase().includes(f) ||
      m.matter_id.toLowerCase().includes(f),
    );
  }, [matters, matterFilter]);

  if (overviewError) {
    return (
      <div className="max-w-6xl mx-auto p-6 text-sm">
        <button onClick={onBack} className="text-zinc-500 mb-4">← Back</button>
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-red-800">
          {overviewError}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="diagnostic-page" className="max-w-7xl mx-auto p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {onBack && (
            <button onClick={onBack} className="text-zinc-500 hover:text-zinc-900" aria-label="Back">
              ←
            </button>
          )}
          <h1 className="text-2xl font-semibold text-zinc-900" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Diagnostic
          </h1>
          <span className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded">
            admin · read-only · refreshes every 30s
          </span>
        </div>
        <div className="flex items-center gap-2">
          <ExportAllMattersButton />
          <button type="button" className={outlineBtn} onClick={refreshOverview}>
            Refresh
          </button>
        </div>
      </div>

      <OverviewPanel overview={overview} />

      <CleanupPanel />

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        <div className="md:col-span-5 lg:col-span-4">
          <MatterListPanel
            matters={filtered}
            filter={matterFilter}
            onFilter={setMatterFilter}
            active={activeMatter}
            onSelect={setActiveMatter}
            total={matters.length}
          />
        </div>
        <div className="md:col-span-7 lg:col-span-8">
          {activeMatter ? (
            <MatterDetailPanel
              matterId={activeMatter}
              detail={detail}
              loading={loadingDetail}
              tab={tab}
              setTab={setTab}
              onReindexed={() => loadDetail(activeMatter)}
            />
          ) : (
            <div className="border border-zinc-200 rounded-md bg-white p-8 text-sm text-zinc-500 text-center">
              Select a matter on the left to inspect.
            </div>
          )}
        </div>
      </div>

      <SessionReplayPanel />
      <AppFooter />
    </div>
  );
}

function OverviewPanel({ overview }: { overview: any }) {
  if (!overview) {
    return <div className="text-sm text-zinc-500">Loading…</div>;
  }
  const pi = overview.pageindex || {};
  const cfg = overview.config || {};
  const byStatus = pi.documents_by_status || {};
  const matterCount = (overview.database?.tables || []).find((t: any) => t.name === 'matter')?.row_count ?? 0;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <Card label="Matters"     value={matterCount} />
        <Card label="Documents"   value={pi.total_documents ?? 0} />
        <Card label="Indexed"     value={`${byStatus.indexed ?? 0} / ${pi.total_documents ?? 0}`} />
        <Card label="Processing"  value={byStatus.processing ?? 0} />
        <Card label="Failed"      value={byStatus.failed ?? 0} />
        <Card label="Active prompts" value={overview.prompts?.active ?? 0} sub={`of ${overview.prompts?.total ?? 0}`} />
      </div>
      <div className="border border-zinc-200 rounded-md bg-white p-3 text-xs text-zinc-700 flex flex-wrap gap-x-6 gap-y-1">
        <span><strong>Draft model:</strong> {cfg.draft_model || '—'}</span>
        <span><strong>Tree model:</strong> {cfg.tree_model || '—'}</span>
        <span><strong>PageIndex:</strong> {cfg.pageindex_enabled ? 'Enabled' : 'Disabled'}</span>
        <span><strong>Config keys:</strong> {cfg.total_keys ?? 0}</span>
      </div>
    </div>
  );
}

/**
 * CleanupPanel — admin database hygiene surface.
 *
 * Two-step flow: preview is always safe (read-only); execute is gated
 * behind a confirm() and only renders once a preview has loaded — so
 * a drafter never lands on the panel and accidentally finds a "Run"
 * button without first seeing what would be deleted.
 *
 * Preview is NOT auto-loaded on mount (the admin must click) — keeps
 * the panel quiet on the diagnostic page until the operator
 * deliberately asks for it.
 */
function CleanupPanel() {
  const [preview, setPreview] = useState<CleanupReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState<CleanupExecuteResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handlePreview = useCallback(async () => {
    setLoading(true);
    setResult(null);
    setError(null);
    try {
      setPreview(await cleanupPreview());
    } catch (e: any) {
      setError(
        e?.response?.status === 403
          ? 'Admin access required.'
          : (apiErrorText(e, 'Failed to load preview.')),
      );
    } finally {
      setLoading(false);
    }
  }, []);

  const handleExecute = useCallback(async () => {
    if (!preview) return;
    const ok = window.confirm(
      `Run database cleanup? This will permanently delete `
      + `${preview.total_rows_to_delete} rows and clear `
      + `${preview.total_blobs_to_clear} JSON blobs. `
      + `This cannot be undone.`,
    );
    if (!ok) return;
    setExecuting(true);
    setError(null);
    try {
      setResult(await cleanupExecute());
      setPreview(null);
    } catch (e: any) {
      setError(
        apiErrorText(e, 'Cleanup failed.'),
      );
    } finally {
      setExecuting(false);
    }
  }, [preview]);

  return (
    <section
      data-testid="cleanup-panel"
      className="border border-zinc-200 rounded-md bg-white p-4"
    >
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-semibold text-zinc-900">
          Database Cleanup
        </h2>
        <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded">
          admin · destructive
        </span>
      </div>
      <p className="text-xs text-zinc-600 mb-3 leading-relaxed">
        Removes orphaned records, old logs, archived documents, and
        stale JSON blobs from completed jobs. Preview first —
        nothing is deleted until you confirm.
      </p>

      {error && (
        <div
          data-testid="cleanup-error"
          className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-800"
        >
          {error}
        </div>
      )}

      <button
        type="button"
        data-testid="cleanup-preview-btn"
        onClick={handlePreview}
        disabled={loading || executing}
        className={outlineBtn + ' mr-2'}
      >
        {loading ? 'Analysing…' : 'Preview cleanup'}
      </button>

      {preview && (
        <div className="mt-3 space-y-2">
          <p
            data-testid="cleanup-preview-summary"
            className="text-xs text-zinc-600"
          >
            Found{' '}
            <strong className="text-zinc-900">
              {preview.total_rows_to_delete}
            </strong>{' '}
            rows to delete and{' '}
            <strong className="text-zinc-900">
              {preview.total_blobs_to_clear}
            </strong>{' '}
            blobs to clear (~{formatBytes(preview.estimated_size_bytes)})
          </p>
          <div className="space-y-1">
            {Object.entries(preview.categories).map(([key, cat]) =>
              cat.count > 0 ? (
                <div
                  key={key}
                  data-testid={`cleanup-category-${key}`}
                  className="flex justify-between text-xs border border-zinc-100 rounded px-3 py-1.5"
                >
                  <span className="text-zinc-700">
                    {cat.description}
                  </span>
                  <span className="text-amber-700 font-mono">
                    {cat.count}
                  </span>
                </div>
              ) : null,
            )}
            {preview.total_rows_to_delete === 0
              && preview.total_blobs_to_clear === 0 && (
              <p
                data-testid="cleanup-nothing-to-do"
                className="text-xs text-zinc-500 italic"
              >
                Nothing to clean up — DB is healthy.
              </p>
            )}
          </div>

          {(preview.total_rows_to_delete > 0
            || preview.total_blobs_to_clear > 0) && (
            <button
              type="button"
              data-testid="cleanup-execute-btn"
              onClick={handleExecute}
              disabled={executing}
              className="mt-2 inline-flex h-9 items-center justify-center rounded-md bg-red-700 px-3 text-sm font-medium text-white hover:bg-red-800 disabled:opacity-50"
            >
              {executing
                ? 'Cleaning…'
                : `Run cleanup (${preview.total_rows_to_delete} rows · ${preview.total_blobs_to_clear} blobs)`}
            </button>
          )}
        </div>
      )}

      {result && (
        <div
          data-testid="cleanup-result"
          className="mt-3 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-900"
        >
          ✓ Cleanup completed at{' '}
          <span className="font-mono">{result.executed_at}</span>{' '}
          by <span className="font-mono">{result.executed_by}</span>.
          {' '}Rows affected:{' '}
          <strong>
            {Object.values(result.deleted).reduce(
              (a, b) => a + (typeof b === 'number' ? b : 0),
              0,
            )}
          </strong>
          .
        </div>
      )}
    </section>
  );
}

function formatBytes(bytes: number): string {
  if (!bytes || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let v = bytes;
  let u = 0;
  while (v >= 1024 && u < units.length - 1) {
    v /= 1024;
    u += 1;
  }
  return `${v.toFixed(v >= 10 || u === 0 ? 0 : 1)} ${units[u]}`;
}

function MatterListPanel({
  matters, filter, onFilter, active, onSelect, total,
}: {
  matters: Matter[]; filter: string; onFilter: (s: string) => void;
  active: string | null; onSelect: (id: string) => void; total: number;
}) {
  return (
    <div className="border border-zinc-200 rounded-md bg-white max-h-[80vh] overflow-y-auto">
      <div className="sticky top-0 bg-white border-b border-zinc-200 p-3 space-y-2">
        <input
          className={inputClass}
          placeholder={`Search ${total} matter(s)…`}
          value={filter}
          onChange={(e) => onFilter(e.target.value)}
        />
      </div>
      <div className="divide-y divide-zinc-100">
        {matters.length === 0 && (
          <div className="text-xs text-zinc-500 p-4 text-center">No matters.</div>
        )}
        {matters.map((m) => (
          <button
            key={m.matter_id}
            type="button"
            onClick={() => onSelect(m.matter_id)}
            className={
              'w-full text-left px-3 py-2 text-sm hover:bg-zinc-50 transition-colors ' +
              (active === m.matter_id ? 'bg-zinc-100' : '')
            }
          >
            <div className="font-medium text-zinc-900 truncate">{m.title || '(untitled)'}</div>
            <div className="flex flex-wrap gap-2 text-[11px] text-zinc-500 mt-0.5">
              <StatusBadge status={m.status || m.current_stage} />
              <span>claims {m.claim_count}/{m.frozen_claim_count} frozen</span>
              <span>docs {m.indexed_document_count}/{m.document_count} indexed</span>
            </div>
            <div className="font-mono text-[10px] text-zinc-400 truncate">{m.matter_id}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function MatterDetailPanel({
  matterId, detail, loading, tab, setTab, onReindexed,
}: {
  matterId: string; detail: any; loading: boolean; tab: Tab;
  setTab: (t: Tab) => void; onReindexed: () => void;
}) {
  if (loading || !detail) {
    return <div className="border border-zinc-200 rounded-md bg-white p-6 text-sm text-zinc-500">Loading detail…</div>;
  }
  if (detail.error) {
    return <div className="border border-red-200 rounded-md bg-red-50 p-3 text-sm text-red-800">{detail.error}</div>;
  }
  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview',   label: 'Overview' },
    { id: 'documents',  label: `Documents (${detail.documents?.length ?? 0})` },
    { id: 'index_pool', label: `Index pool (${detail.indexes?.length ?? 0})` },
    { id: 'claims',     label: `Claims (${detail.claims?.length ?? 0})` },
    { id: 'qa',         label: `Q&A (${detail.qa_rounds?.length ?? 0})` },
    { id: 'qc_log',     label: 'QC log' },
    { id: 'spec_sections', label: `Spec Sections (${detail.spec_section_count ?? 0})` },
    { id: 'figures',    label: `Drawings (${detail.figure_count ?? 0})` },
    { id: 'llm_calls',  label: 'LLM Calls' },
    { id: 'live_query', label: 'Live query' },
    { id: 'tables',     label: 'Raw tables' },
  ];
  return (
    <div className="border border-zinc-200 rounded-md bg-white">
      <div className="flex items-center justify-between border-b border-zinc-200 p-2 gap-2 flex-wrap">
        <div className="flex flex-wrap gap-1">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={
                'px-3 py-1.5 text-xs rounded ' +
                (tab === t.id ? 'bg-[#002FA7] text-white' : 'text-zinc-600 hover:bg-zinc-100')
              }
            >{t.label}</button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <ActivityDownloadButton matterId={matterId} />
          <ExportMatterButton matterId={matterId} />
        </div>
      </div>
      <div className="p-4">
        {tab === 'overview'   && <OverviewTab detail={detail} />}
        {tab === 'documents'  && <DocumentsTab matterId={matterId} detail={detail} onReindexed={onReindexed} />}
        {tab === 'index_pool' && <IndexPoolTab detail={detail} />}
        {tab === 'claims'     && <ClaimsTab detail={detail} />}
        {tab === 'qa'         && <QaTab detail={detail} />}
        {tab === 'qc_log'     && <QcLogTab matterId={matterId} />}
        {tab === 'spec_sections' && <SpecSectionsTab matterId={matterId} />}
        {tab === 'figures'    && <FiguresTab matterId={matterId} />}
        {tab === 'llm_calls'  && <LlmCallsTab matterId={matterId} initialPromptPrefix={null} />}
        {tab === 'live_query' && <LiveQueryTab matterId={matterId} />}
        {tab === 'tables'     && <RawTablesTab />}
      </div>
    </div>
  );
}

function OverviewTab({ detail }: { detail: any }) {
  const m = detail.matter || {};
  return (
    <div className="space-y-3 text-sm">
      <div className="grid grid-cols-2 gap-x-6 gap-y-1">
        {Object.entries(m).map(([k, v]) => (
          <div key={k} className="flex justify-between gap-2 border-b border-zinc-100 py-1">
            <span className="text-xs font-mono text-zinc-500">{k}</span>
            <span className="text-xs text-zinc-800 truncate max-w-[60%]" title={String(v ?? '')}>{String(v ?? '—')}</span>
          </div>
        ))}
      </div>
      <details className="text-xs text-zinc-700">
        <summary className="cursor-pointer text-zinc-500 hover:text-zinc-900">Stage state ({detail.stage_state?.length ?? 0} rows)</summary>
        <pre className="mt-2 bg-zinc-50 p-2 rounded text-[11px] overflow-x-auto">{JSON.stringify(detail.stage_state, null, 2)}</pre>
      </details>
      <details className="text-xs text-zinc-700">
        <summary className="cursor-pointer text-zinc-500 hover:text-zinc-900">Freeze log ({detail.freeze_log?.length ?? 0})</summary>
        <pre className="mt-2 bg-zinc-50 p-2 rounded text-[11px] overflow-x-auto">{JSON.stringify(detail.freeze_log, null, 2)}</pre>
      </details>
    </div>
  );
}

function DocumentsTab({
  matterId, detail, onReindexed,
}: { matterId: string; detail: any; onReindexed: () => void }) {
  const docs: DocRow[] = detail.documents || [];
  const [reindexing, setReindexing] = useState(false);
  const [openDoc, setOpenDoc] = useState<number | null>(null);
  const [fullTree, setFullTree] = useState<any>(null);

  const reindex = async () => {
    setReindexing(true);
    try {
      await diagReindex(matterId);
      onReindexed();
    } finally {
      setReindexing(false);
    }
  };
  const fetchTree = async (id: number) => {
    setOpenDoc(id);
    setFullTree(null);
    try {
      setFullTree(await diagDocumentTree(matterId, id));
    } catch (e: any) {
      setFullTree({ error: apiErrorText(e, 'fetch failed') });
    }
  };
  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <span className="text-xs text-zinc-500">{docs.length} documents</span>
        <button type="button" className={outlineBtn} disabled={reindexing} onClick={reindex}>
          {reindexing ? 'Re-indexing…' : 'Re-index All'}
        </button>
      </div>
      <table className="w-full text-xs">
        <thead className="text-zinc-500">
          <tr>
            <th className="text-left p-2">Filename</th>
            <th className="text-left p-2">Type</th>
            <th className="text-left p-2">Status</th>
            <th className="text-left p-2">Vision</th>
            <th className="text-left p-2">Indexed</th>
            <th className="text-right p-2">Tree</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-100">
          {docs.map((d) => (
            <React.Fragment key={d.id}>
              <tr className="hover:bg-zinc-50 cursor-pointer" onClick={() => fetchTree(d.id)}>
                <td className="p-2 font-mono">{d.doc_name}</td>
                <td className="p-2">{d.doc_type}</td>
                <td className="p-2"><StatusBadge status={d.status} /></td>
                <td className="p-2">{d.vision_processed ? '✓' : '—'}</td>
                <td className="p-2 text-zinc-500">{d.indexed_at ? new Date(d.indexed_at).toLocaleString() : '—'}</td>
                <td className="p-2 text-right">{d.tree_json_size}</td>
              </tr>
              {openDoc === d.id && (
                <tr>
                  <td colSpan={6} className="bg-zinc-50 p-2">
                    <pre className="text-[11px] max-h-[300px] overflow-auto bg-white border border-zinc-200 rounded p-2">
                      {fullTree ? JSON.stringify(fullTree.tree_json ?? fullTree, null, 2) : 'Loading…'}
                    </pre>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function IndexPoolTab({ detail }: { detail: any }) {
  const idx = detail.indexes || [];
  return (
    <table className="w-full text-xs">
      <thead className="text-zinc-500">
        <tr>
          <th className="text-left p-2">Doc Type</th>
          <th className="text-left p-2">Doc Name</th>
          <th className="text-left p-2">Indexed At</th>
          <th className="text-left p-2">Content Preview</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-zinc-100">
        {idx.length === 0 && <tr><td colSpan={4} className="p-3 text-center text-zinc-500">No index rows yet.</td></tr>}
        {idx.map((r: any) => (
          <tr key={r.id}>
            <td className="p-2">{r.doc_type}</td>
            <td className="p-2 font-mono">{r.doc_name}</td>
            <td className="p-2 text-zinc-500">{r.indexed_at ? new Date(r.indexed_at).toLocaleString() : '—'}</td>
            <td className="p-2 text-zinc-700">{r.content_preview}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ClaimsTab({ detail }: { detail: any }) {
  const claims = detail.claims || [];
  const [expanded, setExpanded] = useState<string | null>(null);
  return (
    <table className="w-full text-xs">
      <thead className="text-zinc-500">
        <tr>
          <th className="text-left p-2">#</th>
          <th className="text-left p-2">Type</th>
          <th className="text-left p-2">Category</th>
          <th className="text-left p-2">Depends</th>
          <th className="text-left p-2">Frozen</th>
          <th className="text-left p-2">Version</th>
          <th className="text-left p-2">Text</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-zinc-100">
        {claims.map((c: any) => {
          const rowCls = c.is_frozen
            ? 'bg-emerald-50/40'
            : 'bg-amber-50/40';
          const isOpen = expanded === c.id;
          return (
            <React.Fragment key={c.id}>
              <tr
                className={`${rowCls} cursor-pointer hover:bg-zinc-100/40`}
                onClick={() => setExpanded(isOpen ? null : c.id)}
              >
                <td className={`p-2 ${c.claim_type === 'independent' ? 'font-bold' : ''}`}>{c.claim_number}</td>
                <td className="p-2">{c.claim_type}</td>
                <td className="p-2">{c.claim_category || c.category || '—'}</td>
                <td className="p-2">{c.parent_claim_number ?? c.dependency_on ?? '—'}</td>
                <td className="p-2">{c.is_frozen ? '✓' : '—'}</td>
                <td className="p-2">{c.version ?? 1}</td>
                <td className="p-2 text-zinc-700">{c.text_preview}{c.text_length > 100 && '…'}</td>
              </tr>
              {isOpen && (
                <tr><td colSpan={7} className="bg-zinc-50 p-2 text-[11px] text-zinc-700 whitespace-pre-wrap">
                  {c.text_preview}{c.text_length > 100 && ` (${c.text_length - 100} more chars — fetch via /admin/diagnostic/matters/:id for full text)`}
                </td></tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

function QaTab({ detail }: { detail: any }) {
  const rounds = detail.qa_rounds || [];
  return (
    <table className="w-full text-xs">
      <thead className="text-zinc-500">
        <tr>
          <th className="text-left p-2">Round</th>
          <th className="text-left p-2">Stage</th>
          <th className="text-left p-2">Question</th>
          <th className="text-left p-2">Answer</th>
          <th className="text-left p-2">Material</th>
          <th className="text-left p-2">Outdated</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-zinc-100">
        {rounds.length === 0 && <tr><td colSpan={6} className="p-3 text-center text-zinc-500">No Q&A rounds.</td></tr>}
        {rounds.map((r: any) => (
          <tr key={r.id}>
            <td className="p-2">{r.round_number}</td>
            <td className="p-2">{r.stage}</td>
            <td className="p-2 text-zinc-700">{r.question_preview || `(${r.question_count} questions)`}</td>
            <td className="p-2 text-zinc-700">{r.answer_preview || '—'}</td>
            <td className="p-2">{r.is_material ? '✓' : '—'}</td>
            <td className="p-2">{r.marked_outdated ? '⚠️' : '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * ActivityDownloadButton — the "Matter activity (epi governance)"
 * affordance from Item 4 of the epi Guidelines UI Compliance
 * addendum. Fetches the chronological event trail from
 * /api/matters/{id}/activity and saves it as matter-{id}-activity.json.
 * No inline rendering of the events — the PRD explicitly calls this
 * out as a download-only surface. Sits in the same controls cluster
 * as ExportMatterButton so admins reviewing a matter have both
 * in one place.
 */
function ActivityDownloadButton({ matterId }: { matterId: string }) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const doDownload = async () => {
    setLoading(true);
    setErr(null);
    try {
      const events = await import('@/api/client')
        .then(m => m.getMatterActivity(matterId));
      const blob = new Blob(
        [JSON.stringify(events, null, 2)],
        { type: 'application/json' },
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `matter-${matterId}-activity.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (e: any) {
      setErr(apiErrorText(e, 'Activity download failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      {err && <span className="text-[11px] text-red-700">{err}</span>}
      <button
        type="button"
        data-testid="diag-download-activity"
        onClick={doDownload}
        disabled={loading}
        className={outlineBtn + ' gap-1'}
        title="Download the per-matter activity trail (epi governance) as JSON"
      >
        {loading ? 'Preparing…' : '⬇ Activity JSON (epi)'}
      </button>
    </div>
  );
}


function ExportMatterButton({ matterId }: { matterId: string }) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const doExport = async () => {
    setLoading(true);
    setErr(null);
    try {
      const { filename, blob } = await diagExportMatter(matterId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (e: any) {
      setErr(apiErrorText(e, 'Export failed'));
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="flex items-center gap-2">
      {err && <span className="text-[11px] text-red-700">{err}</span>}
      <button
        type="button"
        data-testid="diag-export-matter"
        onClick={doExport}
        disabled={loading}
        className={outlineBtn + ' gap-1'}
        title="Download full matter JSON including PageIndex trees"
      >
        {loading ? 'Generating…' : '⬇ Export Matter JSON'}
      </button>
    </div>
  );
}


// Workspace-wide bulk export — every matter in the admin's workspace
// in a single ZIP, plus a _manifest.json that lists which ones built
// and which (if any) failed.
function ExportAllMattersButton() {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const doExport = async () => {
    setLoading(true);
    setErr(null);
    try {
      const { filename, blob } = await diagExportAllMatters();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (e: any) {
      setErr(apiErrorText(e, 'Bulk export failed'));
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="flex items-center gap-2">
      {err && <span className="text-[11px] text-red-700">{err}</span>}
      <button
        type="button"
        data-testid="diag-export-all-matters"
        onClick={doExport}
        disabled={loading}
        className={outlineBtn + ' gap-1'}
        title="Download a ZIP of every matter in this workspace as JSON"
      >
        {loading ? 'Bundling…' : '⬇ Export all matters (ZIP)'}
      </button>
    </div>
  );
}


function SessionReplayPanel() {
  const [sessions, setSessions] = useState<UserSessionRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [active, setActive] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const data = await diagListSessions({ limit: 50 });
      setSessions(data.rows || []);
    } catch (e: any) {
      setErr(apiErrorText(e, 'Failed to load sessions'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div
      data-testid="session-replay-panel"
      className="border border-zinc-200 rounded-md bg-white"
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-200">
        <h2 className="text-sm font-semibold text-zinc-900">Session Replay</h2>
        <button type="button" className={outlineBtn} onClick={load} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>
      {err && (
        <div className="m-3 p-2 rounded border border-red-200 bg-red-50 text-sm text-red-800">{err}</div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
        <div className="border-r border-zinc-100 max-h-[640px] overflow-auto">
          {sessions.length === 0 ? (
            <p className="p-3 text-xs text-zinc-500">No sessions recorded yet.</p>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-zinc-50 text-zinc-600 sticky top-0">
                <tr>
                  <th className="p-2 text-left">User</th>
                  <th className="p-2 text-left">Role</th>
                  <th className="p-2 text-left">Started</th>
                  <th className="p-2 text-left">Duration</th>
                  <th className="p-2 text-left">Events</th>
                  <th className="p-2 text-left">Matters</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => {
                  const dur = s.total_duration_ms
                    ? `${Math.floor(s.total_duration_ms / 60000)}m ${Math.floor((s.total_duration_ms % 60000) / 1000)}s`
                    : '—';
                  const start = (s.started_at || '').slice(0, 19).replace('T', ' ');
                  const isActive = active === s.session_id;
                  return (
                    <tr
                      key={s.session_id}
                      data-testid={`session-row-${s.session_id}`}
                      onClick={() => setActive(s.session_id)}
                      className={
                        'border-t border-zinc-100 cursor-pointer hover:bg-zinc-50/60 ' +
                        (isActive ? 'bg-blue-50/60' : '')
                      }
                    >
                      <td className="p-2 font-mono text-[11px] truncate max-w-[160px]">{s.email || s.user_id}</td>
                      <td className="p-2">{s.role || '—'}</td>
                      <td className="p-2 font-mono text-[11px] whitespace-nowrap">{start}</td>
                      <td className="p-2 whitespace-nowrap">{dur}</td>
                      <td className="p-2">{s.event_count}</td>
                      <td className="p-2 text-[11px] truncate max-w-[160px]" title={s.matters_touched?.join(', ')}>
                        {(s.matters_touched || []).length}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        <div className="max-h-[640px] overflow-auto">
          {active ? (
            <SessionReplayView sessionId={active} />
          ) : (
            <p className="p-6 text-sm text-zinc-500 text-center">
              Select a session on the left to replay its event stream.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}


function SessionReplayView({ sessionId }: { sessionId: string }) {
  const [data, setData] = useState<SessionEventsResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setErr(null);
      try {
        const d = await diagSessionEvents(sessionId);
        if (!cancelled) setData(d);
      } catch (e: any) {
        if (!cancelled) setErr(apiErrorText(e, 'Failed to load events'));
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId]);

  if (err) return <div className="m-3 p-2 rounded border border-red-200 bg-red-50 text-sm text-red-800">{err}</div>;
  if (!data) return <p className="p-4 text-sm text-zinc-500">Loading replay…</p>;

  const toggle = (id: number) => {
    setExpanded((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const fmt = (ms: number | null | undefined) => {
    if (ms == null) return '';
    const total = Math.floor(ms / 1000);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const catClass = (c: string) => {
    switch (c) {
      case 'navigation': return 'text-blue-700 bg-blue-50 border-blue-200';
      case 'interaction': return 'text-green-700 bg-green-50 border-green-200';
      case 'llm': return 'text-purple-700 bg-purple-50 border-purple-200';
      default: return 'text-zinc-600 bg-zinc-50 border-zinc-200';
    }
  };

  return (
    <div className="p-3 space-y-3" data-testid="session-replay-view">
      <div className="border border-zinc-200 rounded bg-zinc-50/60 p-2 text-xs space-y-1">
        <div>
          <span className="text-zinc-500">User:</span>{' '}
          <span className="font-mono">{data.session.email || data.session.user_id}</span>{' · '}
          <span>{data.session.role}</span>
        </div>
        <div>
          <span className="text-zinc-500">Started:</span>{' '}
          <span className="font-mono">{data.session.started_at?.slice(0, 19).replace('T', ' ')}</span>
          {data.session.ended_at && (
            <>
              {' → '}
              <span className="font-mono">{data.session.ended_at.slice(0, 19).replace('T', ' ')}</span>
            </>
          )}
          {data.session.total_duration_ms != null && (
            <span className="ml-2 text-zinc-500">({Math.floor((data.session.total_duration_ms || 0) / 1000)}s)</span>
          )}
        </div>
        {(data.session.matters_touched || []).length > 0 && (
          <div>
            <span className="text-zinc-500">Matters:</span>{' '}
            <span className="font-mono text-[11px]">{(data.session.matters_touched || []).join(', ')}</span>
          </div>
        )}
        <div className="text-[11px] text-zinc-600 pt-1 border-t border-zinc-200 mt-1">
          {data.summary.total_events} events · {data.summary.llm_calls} LLM calls
          {data.summary.avg_llm_duration_ms > 0 && ` (avg ${Math.round(data.summary.avg_llm_duration_ms)}ms)`}
          {' · '}
          {data.summary.regenerations} regenerations · {data.summary.manual_edits} manual edits
          {' · '}
          {data.summary.long_pauses.length} long pauses
        </div>
      </div>

      <div className="space-y-1">
        {data.events.map((e) => {
          const isPause = (e.duration_ms || 0) > 60_000;
          const isExpanded = expanded.has(e.id);
          return (
            <div key={e.id}>
              {isPause && (
                <div
                  data-testid={`session-pause-${e.id}`}
                  className="my-1 p-2 text-[11px] rounded border border-amber-300 bg-amber-50 text-amber-800 flex items-center gap-2"
                >
                  ⏸ {Math.floor((e.duration_ms || 0) / 60000)} min {Math.floor(((e.duration_ms || 0) % 60000) / 1000)}s pause — possible hesitation
                </div>
              )}
              <button
                type="button"
                onClick={() => toggle(e.id)}
                data-testid={`session-event-${e.id}`}
                data-category={e.event_category}
                className={
                  'w-full text-left text-xs p-2 rounded border flex items-start gap-2 ' +
                  catClass(e.event_category)
                }
              >
                <span className="font-mono text-[10px] shrink-0">{fmt(e.cumulative_ms)}</span>
                <span className="uppercase text-[10px] font-semibold shrink-0">{e.event_category}</span>
                <span className="flex-1">
                  <span className="font-medium">{e.event_type}</span>
                  {e.element_id && <span className="ml-2 text-[11px] opacity-70 font-mono">{e.element_id}</span>}
                  {e.duration_ms != null && e.event_category === 'llm' && (
                    <span className="ml-2 text-[11px] opacity-70">{e.duration_ms}ms</span>
                  )}
                </span>
              </button>
              {isExpanded && (
                <pre className="text-[10px] font-mono bg-white border border-zinc-200 rounded p-2 mt-1 overflow-x-auto max-h-48">
                  {JSON.stringify({ page: e.page, matter_id: e.matter_id, event_data: e.event_data, duration_ms: e.duration_ms }, null, 2)}
                </pre>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


function QcLogTab({ matterId }: { matterId: string }) {
  const [rows, setRows] = useState<QcLogRow[]>([]);
  const [summary, setSummary] = useState<{ total: number; failed: number }>({ total: 0, failed: 0 });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const data = await diagListQcLog({ matter_id: matterId, limit: 100 });
      setRows(data.rows || []);
      setSummary(data.summary || { total: 0, failed: 0 });
    } catch (e: any) {
      setErr(apiErrorText(e, 'Failed to load QC log'));
    } finally {
      setLoading(false);
    }
  }, [matterId]);

  useEffect(() => { load(); }, [load]);

  const toggle = (id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <div data-testid="qc-log-tab">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-zinc-700">
          <span className="font-medium">{summary.total}</span> QC run{summary.total === 1 ? '' : 's'} ·{' '}
          <span className={summary.failed > 0 ? 'text-amber-700 font-medium' : 'text-zinc-600'}>
            {summary.failed} parse failure{summary.failed === 1 ? '' : 's'}
          </span>
        </div>
        <button type="button" className={outlineBtn} onClick={load} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>
      {err && (
        <div className="mb-2 p-2 rounded border border-red-200 bg-red-50 text-sm text-red-800">{err}</div>
      )}
      {rows.length === 0 ? (
        <p className="text-sm text-zinc-500">No QC runs recorded for this matter yet.</p>
      ) : (
        <table className="w-full text-xs border border-zinc-200">
          <thead className="bg-zinc-50 text-zinc-600">
            <tr>
              <th className="p-2 text-left">When</th>
              <th className="p-2 text-left">Status</th>
              <th className="p-2 text-left">Attempt</th>
              <th className="p-2 text-left">Findings</th>
              <th className="p-2 text-left">Model</th>
              <th className="p-2 text-left">Reason</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const open = expanded.has(r.id);
              return (
                <React.Fragment key={r.id}>
                  <tr
                    className="border-t border-zinc-100 hover:bg-zinc-50/60 cursor-pointer"
                    onClick={() => toggle(r.id)}
                    data-testid={`qc-log-row-${r.id}`}
                  >
                    <td className="p-2 font-mono text-[11px] whitespace-nowrap">
                      {r.created_at?.slice(0, 19).replace('T', ' ')}
                    </td>
                    <td className="p-2">
                      <span
                        className={
                          'px-1.5 py-0.5 rounded text-[10px] border ' +
                          (r.parse_status === 'ok'
                            ? 'bg-green-50 border-green-200 text-green-800'
                            : 'bg-amber-50 border-amber-200 text-amber-800')
                        }
                      >
                        {r.parse_status}
                      </span>
                    </td>
                    <td className="p-2">{r.attempt_number}</td>
                    <td className="p-2">{r.findings_count}</td>
                    <td className="p-2 font-mono text-[11px] truncate max-w-[220px]" title={r.model_used || ''}>
                      {r.model_used || '—'}
                    </td>
                    <td className="p-2 text-zinc-700 truncate max-w-[320px]" title={r.reason || ''}>
                      {r.reason || (r.parse_status === 'ok' ? '—' : '')}
                    </td>
                  </tr>
                  {open && (
                    <tr className="border-t border-zinc-100 bg-zinc-50/60">
                      <td className="p-2 text-[11px] text-zinc-700" colSpan={6}>
                        <div className="font-mono whitespace-pre-wrap break-all max-h-48 overflow-auto">
                          {r.raw_response_preview || '(no raw preview recorded)'}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}


function LlmCallsTab({
  matterId,
  initialPromptPrefix = null,
}: {
  matterId: string;
  /** Spec Sections / Drawings tabs deep-link here with the
   *  matching prefix preselected. Null preserves the original
   *  behaviour where the filter chip starts unset. */
  initialPromptPrefix?: string | null;
}) {
  const [data, setData] = useState<LlmCallsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [blankOnly, setBlankOnly] = useState(false);
  const [stageFilter, setStageFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  // Module filter — separate from ``stageFilter`` because spec_*
  // and drawing_* call rows don't carry a stage column entry.
  // Maps directly onto the backend ``prompt_prefix`` query arg.
  const [moduleFilter, setModuleFilter] = useState<string>(
    initialPromptPrefix ?? '',
  );
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const resp = await diagMatterLlmCalls(matterId, {
        blank_only: blankOnly,
        stage: stageFilter || undefined,
        parse_status: statusFilter || undefined,
        prompt_prefix: moduleFilter || undefined,
        limit: 500,
      });
      setData(resp);
    } catch (e: any) {
      setErr(apiErrorText(e, 'Failed to load LLM calls'));
    } finally {
      setLoading(false);
    }
  }, [matterId, blankOnly, stageFilter, statusFilter, moduleFilter]);

  useEffect(() => { load(); }, [load]);

  const toggle = (id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const summary = data?.summary;
  const calls = data?.calls || [];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="font-mono text-zinc-500">
          {loading ? 'Loading…' : `${calls.length} call${calls.length === 1 ? '' : 's'}`}
        </span>
        {summary && (
          <span className="text-zinc-600">
            · avg {summary.avg_duration_ms}ms
            {summary.blank_incidents > 0 && (
              <span className="ml-2 text-amber-700">
                ⚠ {summary.blank_incidents} with blank placeholders
              </span>
            )}
            {summary.parse_failures > 0 && (
              <span className="ml-2 text-red-700">
                ✗ {summary.parse_failures} parse failures
              </span>
            )}
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <label className="inline-flex items-center gap-1 text-zinc-600">
            <input
              type="checkbox"
              checked={blankOnly}
              onChange={(e) => setBlankOnly(e.target.checked)}
            />
            Show blanks only
          </label>
          <select
            value={stageFilter}
            onChange={(e) => setStageFilter(e.target.value)}
            className="text-xs border border-zinc-300 rounded-sm px-1.5 py-0.5"
          >
            <option value="">All stages</option>
            <option value="stage_1">Stage 1</option>
            <option value="stage_2">Stage 2</option>
            <option value="stage_2_5">Stage 2.5</option>
            <option value="stage_3">Stage 3</option>
            <option value="stage_4">Stage 4</option>
          </select>
          {/* Module filter — spec_* and drawing_* prompt rows
              don't have a stage entry, so they're isolated by
              prompt-key prefix instead. */}
          <select
            value={moduleFilter}
            onChange={(e) => setModuleFilter(e.target.value)}
            className="text-xs border border-zinc-300 rounded-sm px-1.5 py-0.5"
            data-testid="llm-calls-module-filter"
          >
            <option value="">All modules</option>
            <option value="spec_">Spec</option>
            <option value="drawing_">Drawings</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="text-xs border border-zinc-300 rounded-sm px-1.5 py-0.5"
          >
            <option value="">Any status</option>
            <option value="ok">ok</option>
            <option value="json_parse_failed">json_parse_failed</option>
            <option value="empty_response">empty_response</option>
            <option value="api_error">api_error</option>
          </select>
          <button
            type="button"
            onClick={load}
            className="text-xs px-2 py-0.5 rounded-sm border border-zinc-300 hover:bg-zinc-50"
          >Refresh</button>
        </div>
      </div>
      {err && (
        <div className="p-2 text-xs bg-red-50 border border-red-200 rounded-sm text-red-800">
          {err}
        </div>
      )}
      {!loading && calls.length === 0 && !err && (
        <div className="p-3 text-xs text-zinc-500 border border-zinc-200 rounded-sm">
          No LLM calls recorded{blankOnly ? ' with blank placeholders' : ''} for this matter yet.
        </div>
      )}
      <ul className="space-y-1.5">
        {calls.map((c: LlmCallRow) => {
          const isOpen = expanded.has(c.id);
          const blanks = c.blank_placeholders || [];
          const statusTone =
            c.parse_status === 'ok'
              ? 'text-green-700'
              : c.parse_status === 'empty_response'
                ? 'text-amber-700'
                : 'text-red-700';
          return (
            <li key={c.id} className="border border-zinc-200 rounded-sm bg-white text-xs">
              <button
                type="button"
                onClick={() => toggle(c.id)}
                className="w-full flex flex-wrap items-center gap-2 px-3 py-1.5 text-left hover:bg-zinc-50"
              >
                <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 shrink-0">
                  {c.stage || '—'}
                </span>
                <span className="font-mono text-zinc-800 shrink-0">{c.prompt_key || '(ad-hoc)'}</span>
                <span className="text-zinc-500 shrink-0">{c.model}</span>
                <span className="text-zinc-500 shrink-0">{c.duration_ms ?? '—'}ms</span>
                <span className={`font-mono text-[10px] shrink-0 ${statusTone}`}>
                  {c.parse_status}
                </span>
                {blanks.length > 0 && (
                  <span className="inline-flex items-center gap-1 text-amber-700 shrink-0">
                    ⚠ blank: {blanks.join(', ')}
                  </span>
                )}
                <span className="ml-auto text-[10px] text-zinc-400 shrink-0">
                  {new Date(c.created_at).toLocaleString()}
                </span>
              </button>
              {isOpen && (
                <div className="border-t border-zinc-100 px-3 py-2 space-y-2 bg-zinc-50/40">
                  <div className="text-[10px] font-mono text-zinc-500">
                    placeholders:&nbsp;
                    {Object.keys(c.placeholders_used || {}).length === 0 ? '—' : (
                      Object.entries(c.placeholders_used).map(([k, n]) => (
                        <span key={k} className={`mr-2 ${n === 0 ? 'text-amber-700' : 'text-zinc-700'}`}>
                          {k}({n})
                          {n === 0 ? '⚠' : ''}
                        </span>
                      ))
                    )}
                  </div>
                  {c.parse_error && (
                    <div className="p-2 text-[10px] bg-red-50 border border-red-200 rounded-sm text-red-800">
                      parse_error: {c.parse_error}
                    </div>
                  )}
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
                      final_prompt ({c.prompt_tokens_estimate ?? '?'} tokens est.)
                    </div>
                    <pre className="max-h-64 overflow-auto whitespace-pre-wrap text-[11px] leading-snug bg-white border border-zinc-200 rounded-sm p-2">
                      {c.final_prompt}
                    </pre>
                  </div>
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
                      response_text ({c.response_tokens_estimate ?? '?'} tokens est.)
                    </div>
                    <pre className="max-h-64 overflow-auto whitespace-pre-wrap text-[11px] leading-snug bg-white border border-zinc-200 rounded-sm p-2">
                      {c.response_text || '(empty)'}
                    </pre>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}


function LiveQueryTab({ matterId }: { matterId: string }) {
  const [query, setQuery] = useState('what is the operating voltage');
  const [docTypes, setDocTypes] = useState<string[]>(['idf', 'frozen_claim']);
  const [maxNodes, setMaxNodes] = useState(5);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<any>(null);

  const toggle = (t: string) =>
    setDocTypes((prev) => prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]);

  const run = async () => {
    setRunning(true);
    setResult(null);
    try {
      setResult(await diagLiveQuery(matterId, { query, doc_types: docTypes, max_nodes: maxNodes }));
    } catch (e: any) {
      setResult({ error: apiErrorText(e, 'Query failed') });
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <label className="text-xs text-zinc-500">Query</label>
        <input className={inputClass} value={query} onChange={(e) => setQuery(e.target.value)} />
      </div>
      <div>
        <div className="text-xs text-zinc-500 mb-1">Doc types</div>
        <div className="flex flex-wrap gap-2">
          {DOC_TYPES.map((t) => (
            <label key={t} className={`text-xs px-2 py-1 rounded cursor-pointer border ${docTypes.includes(t) ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'border-zinc-200'}`}>
              <input
                type="checkbox"
                className="hidden"
                checked={docTypes.includes(t)}
                onChange={() => toggle(t)}
              />
              {t}
            </label>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-3">
        <label className="text-xs text-zinc-500">Max nodes: {maxNodes}</label>
        <input type="range" min={1} max={10} value={maxNodes} onChange={(e) => setMaxNodes(parseInt(e.target.value, 10))} className="flex-1" />
        <button type="button" className={primaryBtn} disabled={running} onClick={run}>
          {running ? 'Running…' : 'Run Query'}
        </button>
      </div>

      {result && result.error && (
        <div className="text-xs text-red-700">{result.error}</div>
      )}
      {result && !result.error && (
        <div className="space-y-3">
          <div className="text-xs text-zinc-500">
            {result.result_count} results · ~{result.context_tokens_estimate} tokens · {result.duration_ms}ms
          </div>
          {(result.results || []).map((r: any, i: number) => (
            <div key={i} className="border border-zinc-200 rounded-md p-3 bg-zinc-50">
              <div className="flex justify-between text-[11px] text-zinc-500 mb-1">
                <span>{r.source} · {r.section} · p.{r.page}</span>
                <span className="bg-zinc-200 px-1.5 py-0.5 rounded">{r.doc_type || 'n/a'}</span>
              </div>
              <pre className="text-xs text-zinc-800 whitespace-pre-wrap">{r.content}</pre>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RawTablesTab() {
  const [list, setList] = useState<any[]>([]);
  const [active, setActive] = useState<string>('');
  const [sample, setSample] = useState<any>(null);

  useEffect(() => {
    diagTables().then((d) => {
      setList(d.tables || []);
      if ((d.tables || []).length && !active) setActive(d.tables[0].table_name);
    }).catch(() => setList([]));
  }, [active]);

  useEffect(() => {
    if (!active) return;
    diagTableSample(active, 20).then(setSample).catch((e) =>
      setSample({ error: apiErrorText(e, 'fetch failed') }),
    );
  }, [active]);

  const meta = list.find((t) => t.table_name === active);

  return (
    <div className="space-y-3">
      <select className={inputClass} value={active} onChange={(e) => setActive(e.target.value)}>
        {list.map((t) => (
          <option key={t.table_name} value={t.table_name}>
            {t.table_name} ({t.row_count} rows)
          </option>
        ))}
      </select>
      {meta && (
        <div>
          <div className="text-xs text-zinc-500 mb-1">Columns</div>
          <table className="w-full text-xs border border-zinc-200">
            <thead className="bg-zinc-50">
              <tr>
                <th className="text-left p-1.5">Name</th>
                <th className="text-left p-1.5">Type</th>
                <th className="text-left p-1.5">Nullable</th>
                <th className="text-left p-1.5">Default</th>
              </tr>
            </thead>
            <tbody>
              {meta.columns.map((c: any) => (
                <tr key={c.name} className="border-t border-zinc-100">
                  <td className="p-1.5 font-mono">{c.name}</td>
                  <td className="p-1.5">{c.type}</td>
                  <td className="p-1.5">{c.nullable ? 'YES' : 'NO'}</td>
                  <td className="p-1.5 text-zinc-500">{String(c.default ?? '')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {sample && sample.error && <div className="text-xs text-red-700">{sample.error}</div>}
      {sample && !sample.error && (
        <details>
          <summary className="cursor-pointer text-xs text-zinc-500">Sample rows ({sample.rows?.length ?? 0} of {sample.row_count})</summary>
          <pre className="mt-2 text-[11px] bg-zinc-50 p-2 rounded max-h-[400px] overflow-auto">
            {JSON.stringify(sample.rows, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}


// ─── Spec Sections tab ─────────────────────────────────────────────
//
// Read-only diagnostic view over spec_sections + spec_paragraphs.
// Each row collapses to a one-line summary; clicking expands it
// to the per-paragraph body + the recent llm_call_log rows whose
// prompt_key was used to generate any of the paragraphs. The
// "View raw LLM call" affordance navigates back to the LLM Calls
// tab and reuses its existing filter UX.

function SpecSectionsTab({ matterId }: { matterId: string }) {
  const [sections, setSections] = useState<SpecSectionSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [detail, setDetail] = useState<SpecSectionDetailResponse | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const resp = await diagMatterSpecSections(matterId);
      setSections(resp.sections || []);
    } catch (e: any) {
      setErr(apiErrorText(e, 'Failed to load spec sections'));
    } finally {
      setLoading(false);
    }
  }, [matterId]);

  useEffect(() => { void load(); }, [load]);

  const onExpand = useCallback(async (kind: string) => {
    if (expanded === kind) {
      setExpanded(null);
      setDetail(null);
      return;
    }
    setExpanded(kind);
    setDetail(null);
    setDetailLoading(true);
    try {
      const resp = await diagMatterSpecSectionDetail(matterId, kind);
      setDetail(resp);
    } catch (e: any) {
      setErr(apiErrorText(e, 'Failed to load section detail'));
    } finally {
      setDetailLoading(false);
    }
  }, [expanded, matterId]);

  if (loading) return <div className="text-xs text-zinc-500">Loading…</div>;
  if (err) return <div className="text-xs text-red-700">{err}</div>;
  if (!sections.length) {
    return (
      <div className="text-xs text-zinc-500">
        No spec sections drafted yet. Run Stage 5 (spec drafting)
        from the Stage navigation to generate.
      </div>
    );
  }
  return (
    <div className="space-y-2 text-xs" data-testid="diag-spec-sections">
      {sections.map((s) => {
        const isOpen = expanded === s.section_kind;
        const status =
          s.is_excluded ? 'excluded'
          : s.word_count > 0 ? 'drafted'
          : 'empty';
        return (
          <div
            key={s.id}
            className="rounded-sm border border-zinc-200 bg-white"
            data-testid={`diag-spec-row-${s.section_kind}`}
          >
            <button
              type="button"
              className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-zinc-50"
              onClick={() => void onExpand(s.section_kind)}
              data-testid={`diag-spec-row-toggle-${s.section_kind}`}
            >
              <span className="font-mono text-zinc-500 shrink-0">
                #{s.sequence_num}
              </span>
              <span className="font-semibold text-zinc-900 truncate">
                {s.title}
              </span>
              <span className="ml-auto inline-flex items-center gap-2 shrink-0">
                <span className={
                  'rounded-sm px-1.5 py-0.5 text-[10px] font-mono uppercase '
                  + (status === 'drafted'
                    ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
                    : status === 'excluded'
                      ? 'bg-zinc-100 text-zinc-600'
                      : 'bg-amber-50 text-amber-700 ring-1 ring-amber-200')
                }>
                  {status}
                </span>
                <span className="text-zinc-500">
                  {s.word_count} word{s.word_count === 1 ? '' : 's'}
                </span>
                <span className="text-zinc-500">
                  {s.paragraph_count} para{s.paragraph_count === 1 ? '' : 's'}
                </span>
                {s.is_auto_generated && (
                  <span className="rounded-sm bg-zinc-100 px-1.5 py-0.5 text-[10px] font-mono text-zinc-600">
                    auto
                  </span>
                )}
                {s.is_frozen && (
                  <span className="rounded-sm bg-blue-50 px-1.5 py-0.5 text-[10px] font-mono text-blue-700 ring-1 ring-blue-200">
                    frozen
                  </span>
                )}
              </span>
            </button>
            {isOpen && (
              <div className="border-t border-zinc-200 bg-zinc-50 px-3 py-2 space-y-2">
                {detailLoading && (
                  <div className="text-zinc-500">Loading detail…</div>
                )}
                {detail && detail.section.section_kind === s.section_kind && (
                  <>
                    <div className="text-[11px] text-zinc-500">
                      Section kind: <span className="font-mono">{detail.section.section_kind}</span>
                      {detail.section.statute_ref && (
                        <> · Statute: <span className="font-mono">{detail.section.statute_ref}</span></>
                      )}
                    </div>
                    {detail.paragraphs.length === 0 && (
                      <div className="italic text-zinc-500">
                        No paragraphs yet — section is a header-only stub.
                      </div>
                    )}
                    {detail.paragraphs.map((p) => (
                      <div key={p.id} className="rounded-sm border border-zinc-200 bg-white p-2">
                        <div className="mb-1 flex items-center gap-2 text-[10px] font-mono text-zinc-500">
                          <span>¶ {p.paragraph_number}</span>
                          <span>·</span>
                          <span>{p.word_count} words</span>
                          {p.last_generated_prompt_key && (
                            <>
                              <span>·</span>
                              <span className="text-zinc-700">
                                {p.last_generated_prompt_key}
                              </span>
                            </>
                          )}
                          <span className="ml-auto">{p.state}</span>
                        </div>
                        <pre className="whitespace-pre-wrap text-[11px] text-zinc-800">
                          {p.body_text || <span className="italic text-zinc-500">(empty)</span>}
                        </pre>
                      </div>
                    ))}
                    {detail.related_llm_calls.length > 0 && (
                      <div>
                        <div className="mb-1 text-[10px] font-mono uppercase text-zinc-500">
                          Recent LLM calls for this section
                        </div>
                        <table className="w-full text-[11px]">
                          <thead className="text-left text-zinc-500">
                            <tr>
                              <th className="px-1 py-0.5">Prompt</th>
                              <th className="px-1 py-0.5">Model</th>
                              <th className="px-1 py-0.5">Status</th>
                              <th className="px-1 py-0.5">Latency</th>
                              <th className="px-1 py-0.5">When</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detail.related_llm_calls.map((c) => (
                              <tr key={c.id} className="border-t border-zinc-100">
                                <td className="px-1 py-0.5 font-mono">{c.prompt_key}</td>
                                <td className="px-1 py-0.5 font-mono">{c.model || '—'}</td>
                                <td className="px-1 py-0.5 font-mono">{c.parse_status || '—'}</td>
                                <td className="px-1 py-0.5">{c.duration_ms ? `${c.duration_ms}ms` : '—'}</td>
                                <td className="px-1 py-0.5 text-zinc-500">{c.created_at}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}


// ─── Drawings tab ──────────────────────────────────────────────────
//
// Read-only diagnostic view over drawing_figures. Each row collapses
// to a one-line summary; clicking expands it to the analyze bundle
// + claim_prime JSON + canvas-state counts + recent drawing_*
// llm_call_log rows. Stub admin panel at the bottom lists prompt
// keys that are seeded but have no backend caller (Phase 7 / 8 land
// these as separate slices).

function FiguresTab({ matterId }: { matterId: string }) {
  const [figures, setFigures] = useState<FigureSummary[]>([]);
  const [stubs, setStubs] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [detail, setDetail] = useState<FigureDetailResponse | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const resp = await diagMatterFigures(matterId);
      setFigures(resp.figures || []);
      setStubs(resp.stub_prompt_keys || []);
    } catch (e: any) {
      setErr(apiErrorText(e, 'Failed to load figures'));
    } finally {
      setLoading(false);
    }
  }, [matterId]);

  useEffect(() => { void load(); }, [load]);

  const onExpand = useCallback(async (id: string) => {
    if (expanded === id) {
      setExpanded(null);
      setDetail(null);
      return;
    }
    setExpanded(id);
    setDetail(null);
    setDetailLoading(true);
    try {
      const resp = await diagMatterFigureDetail(matterId, id);
      setDetail(resp);
    } catch (e: any) {
      setErr(apiErrorText(e, 'Failed to load figure detail'));
    } finally {
      setDetailLoading(false);
    }
  }, [expanded, matterId]);

  if (loading) return <div className="text-xs text-zinc-500">Loading…</div>;
  if (err) return <div className="text-xs text-red-700">{err}</div>;
  return (
    <div className="space-y-3 text-xs" data-testid="diag-figures">
      {figures.length === 0 && (
        <div className="text-zinc-500">
          No figures generated yet. Run Stage 5 drawings module
          from the Stage navigation.
        </div>
      )}
      {figures.map((f) => {
        const isOpen = expanded === f.id;
        return (
          <div
            key={f.id}
            className="rounded-sm border border-zinc-200 bg-white"
            data-testid={`diag-fig-row-${f.id}`}
          >
            <button
              type="button"
              className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-zinc-50"
              onClick={() => void onExpand(f.id)}
              data-testid={`diag-fig-row-toggle-${f.id}`}
            >
              <span className="font-mono text-zinc-500 shrink-0">
                #{f.sequence_num}
              </span>
              <span className="font-semibold text-zinc-900 shrink-0">
                {f.fig_label}
              </span>
              <span className="text-zinc-500 truncate">
                {f.origin || 'unknown origin'}
                {f.generator && <> · {f.generator}</>}
              </span>
              <span className="ml-auto inline-flex items-center gap-2 shrink-0">
                <span className="text-zinc-500">
                  {f.numeral_count} numeral{f.numeral_count === 1 ? '' : 's'}
                </span>
                {f.has_analyze && (
                  <span className="rounded-sm bg-emerald-50 px-1.5 py-0.5 text-[10px] font-mono text-emerald-700 ring-1 ring-emerald-200">
                    analyzed
                  </span>
                )}
                {f.is_vectorized && (
                  <span className="rounded-sm bg-zinc-100 px-1.5 py-0.5 text-[10px] font-mono text-zinc-600">
                    vector
                  </span>
                )}
                {f.is_frozen && (
                  <span className="rounded-sm bg-blue-50 px-1.5 py-0.5 text-[10px] font-mono text-blue-700 ring-1 ring-blue-200">
                    frozen
                  </span>
                )}
              </span>
            </button>
            {isOpen && (
              <div className="border-t border-zinc-200 bg-zinc-50 px-3 py-2 space-y-2">
                {detailLoading && (
                  <div className="text-zinc-500">Loading detail…</div>
                )}
                {detail && detail.figure.id === f.id && (
                  <>
                    {detail.figure.imported_description && (
                      <div className="rounded-sm border border-zinc-200 bg-white p-2">
                        <div className="mb-1 text-[10px] font-mono uppercase text-zinc-500">
                          Description
                        </div>
                        <p className="whitespace-pre-wrap text-zinc-800">
                          {detail.figure.imported_description}
                        </p>
                      </div>
                    )}
                    <div className="rounded-sm border border-zinc-200 bg-white p-2">
                      <div className="mb-1 text-[10px] font-mono uppercase text-zinc-500">
                        Canvas state
                      </div>
                      <div className="flex flex-wrap gap-2 text-[11px] text-zinc-700">
                        {Object.keys(detail.canvas_counts).length === 0 && (
                          <span className="italic text-zinc-500">(empty)</span>
                        )}
                        {Object.entries(detail.canvas_counts).map(([k, n]) => (
                          <span key={k}>
                            <span className="font-mono">{k}</span>: {n}
                          </span>
                        ))}
                      </div>
                    </div>
                    {detail.figure.analyze_bundle_json && (
                      <details>
                        <summary className="cursor-pointer text-[10px] font-mono uppercase text-zinc-500">
                          Analyze bundle JSON
                        </summary>
                        <pre className="mt-1 max-h-[240px] overflow-auto rounded-sm bg-white p-2 text-[11px]">
                          {JSON.stringify(detail.figure.analyze_bundle_json, null, 2)}
                        </pre>
                      </details>
                    )}
                    {detail.figure.claim_prime_json && (
                      <details>
                        <summary className="cursor-pointer text-[10px] font-mono uppercase text-zinc-500">
                          Claim-prime JSON
                        </summary>
                        <pre className="mt-1 max-h-[240px] overflow-auto rounded-sm bg-white p-2 text-[11px]">
                          {JSON.stringify(detail.figure.claim_prime_json, null, 2)}
                        </pre>
                      </details>
                    )}
                    {detail.related_llm_calls.length > 0 && (
                      <div>
                        <div className="mb-1 text-[10px] font-mono uppercase text-zinc-500">
                          Recent drawing_* LLM calls (matter-wide)
                        </div>
                        <table className="w-full text-[11px]">
                          <thead className="text-left text-zinc-500">
                            <tr>
                              <th className="px-1 py-0.5">Prompt</th>
                              <th className="px-1 py-0.5">Model</th>
                              <th className="px-1 py-0.5">Status</th>
                              <th className="px-1 py-0.5">Latency</th>
                              <th className="px-1 py-0.5">When</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detail.related_llm_calls.map((c) => (
                              <tr key={c.id} className="border-t border-zinc-100">
                                <td className="px-1 py-0.5 font-mono">{c.prompt_key}</td>
                                <td className="px-1 py-0.5 font-mono">{c.model || '—'}</td>
                                <td className="px-1 py-0.5 font-mono">{c.parse_status || '—'}</td>
                                <td className="px-1 py-0.5">{c.duration_ms ? `${c.duration_ms}ms` : '—'}</td>
                                <td className="px-1 py-0.5 text-zinc-500">{c.created_at}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
      {stubs.length > 0 && (
        <div
          className="rounded-sm border border-amber-200 bg-amber-50 px-3 py-2"
          data-testid="diag-figures-stub-panel"
        >
          <div className="mb-1 text-[10px] font-mono uppercase text-amber-800">
            Stub prompts — seeded but no backend caller
          </div>
          <ul className="list-disc pl-5 text-[11px] text-amber-900">
            {stubs.map((k) => (
              <li key={k} className="font-mono">{k}</li>
            ))}
          </ul>
          <p className="mt-1 text-[10px] text-amber-800">
            Phase 7 / Phase 8 wire each handler in a separate slice. The
            prompt rows exist in PROTECTION_METADATA + the seed JSON;
            the matter-detail tab shows them here so the team can see
            which handlers still owe wiring.
          </p>
        </div>
      )}
    </div>
  );
}
